// Printable run sheet for one press lane — the hard copy Pre-Press works from
// to prepare plates and materials ahead of the run. Lists every not-yet-finished
// job on the press, top to bottom in schedule order, with the fields they need
// to pull stock and image plates.
//
// It is also an EDITOR, not just a printout. The sheet is the one place the
// whole queue for a press is legible as a list, so this is where a planner
// naturally wants to say "run that one first" — rows drag up and down (or step
// with ▲▼), which writes the same `sequence` move the board's drag-drop makes.
// One PATCH per move, so the Gantt, the projection and this sheet stay one
// schedule rather than two views that can disagree.
//
// Printing is done with a scoped @media print rule rather than a popup window:
// the dialog already has the fully-resolved data (including Cloudflow
// separations), and a popup would be blocked or lose the styling.
import React from 'react';
import {
  Box, Dialog, DialogContent, DialogActions, Button, GlobalStyles, IconButton, Tooltip,
  ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import PrintRoundedIcon from '@mui/icons-material/PrintRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import DragIndicatorRoundedIcon from '@mui/icons-material/DragIndicatorRounded';
import KeyboardArrowUpRoundedIcon from '@mui/icons-material/KeyboardArrowUpRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { DowntimeWindow } from '../productionTypes';
import { GanttEntry, GanttLane } from './ganttTypes';
import { TOKENS, PLACING_TIMEOUT_MS, RUN_SHEET_HORIZON_HRS } from './ganttConfig';
import { contrastText } from './barStyle';
import { fmtClock } from './GanttBar';
import {
  isReorderable, moveItem, finalIndex, sequenceForMove, queueAnchor, cascadeStarts,
  applyPendingOrder, sameOrder,
} from './runSheetOrder';

interface Props {
  open: boolean;
  onClose: () => void;
  lane: GanttLane | null;
  entries: GanttEntry[];
  base: Date;
  /** Raw downtime rules — used to pause the provisional times across closures. */
  windows: DowntimeWindow[];
  /** Now, in minutes from base — the floor for the provisional cascade. */
  nowAbs: number;
  /** Commit a reorder: give `entry` the sequence `toSequence`. */
  onReorder: (entry: GanttEntry, toSequence: number) => void;
}

const PRINT_ROOT = 'press-run-sheet-print-root';

// Only what Pre-Press still has to prepare — finished and cancelled runs are
// noise on a forward-looking prep sheet.
const isUpcoming = (e: GanttEntry) => e.status === 'planned' || e.status === 'in_progress';

const dayLabel = (base: Date, absStart: number) => {
  const d = new Date(base.getTime() + absStart * 60000);
  return d.toLocaleDateString('en-NZ', { weekday: 'short', day: 'numeric', month: 'short' });
};

const Th: React.FC<{ children: React.ReactNode; align?: 'left' | 'right'; className?: string }> = ({
  children, align = 'left', className,
}) => (
  <Box component="th" className={className} sx={{
    textAlign: align, fontSize: 9.5, fontWeight: 700, letterSpacing: '0.04em',
    textTransform: 'uppercase', color: TOKENS.ink3, borderBottom: `1.5px solid ${TOKENS.ink3}`,
    py: 0.5, px: 0.75, whiteSpace: 'nowrap',
  }}>{children}</Box>
);

const Td: React.FC<{
  children: React.ReactNode; align?: 'left' | 'right'; mono?: boolean; bold?: boolean;
  className?: string;
}> = ({ children, align = 'left', mono, bold, className }) => (
  <Box component="td" className={className} sx={{
    textAlign: align, fontSize: 10.5, py: 0.6, px: 0.75, verticalAlign: 'top',
    borderBottom: `1px solid ${TOKENS.lineSoft}`, color: TOKENS.ink,
    fontFamily: mono ? TOKENS.monoFont : undefined,
    fontWeight: bold ? 700 : undefined,
    fontVariantNumeric: align === 'right' ? 'tabular-nums' : undefined,
  }}>{children}</Box>
);

export default function PressRunSheetDialog({
  open, onClose, lane, entries, base, windows, nowAbs, onReorder,
}: Props) {
  // Optimistic row order (entry ids) held between letting go and the refetch
  // landing. Without it the sheet snaps back to the old order for the length of
  // the round trip, which reads as "the drag didn't take".
  const [pendingIds, setPendingIds] = React.useState<number[] | null>(null);
  // Row being dragged (index into the planned block) and the gap it would drop
  // into (0..len, "before row k").
  const [dragIdx, setDragIdx] = React.useState<number | null>(null);
  const [insertAt, setInsertAt] = React.useState<number | null>(null);

  const laneEntries = React.useMemo(
    () => entries.filter((e) => e.press === lane?.code),
    [entries, lane?.code],
  );

  // Horizon filter — cap the sheet at jobs STARTING within the next N hours.
  // null = the whole queue. Deliberately kept across close/open: a planner who
  // prints 48h sheets prints 48h sheets. The cutoff is from NOW, not from a
  // shift boundary, so "next 24h" means what it says whenever it's printed.
  const [horizonHr, setHorizonHr] = React.useState<number | null>(null);
  const cutoffAbs = horizonHr == null ? null : nowAbs + horizonHr * 60;

  // Order by absStart — the SAME value GanttBar positions each bar at — so the
  // printed list reads top-to-bottom in the order the lane reads left-to-right.
  //
  // Sorting on `sequence` here was wrong once anything was running. The backend
  // projection cascades in REALITY order, not sequence order (compute_projection:
  // completed → in_progress → not-started), because jobs get run out of plan
  // order and the live job can sit at a HIGHER sequence than jobs still waiting
  // behind it. In that state the board draws the running job first while the
  // sheet printed it mid-list, telling Pre-Press to prepare jobs the press had
  // already moved past. sequence stays as the tiebreak for equal starts.
  const upcomingAll = React.useMemo(
    () => laneEntries
      .filter(isUpcoming)
      .sort((a, b) => a.absStart - b.absStart || a.sequence - b.sequence),
    [laneEntries],
  );

  // The horizon trims a time-sorted list, so what survives is a PREFIX of the
  // planned queue — which is why reordering below stays safe under the filter:
  // sequenceForMove only ever permutes the sequence values of the rows it can
  // see, and those are contiguous at the head of the lane.
  const upcoming = React.useMemo(
    () => (cutoffAbs == null ? upcomingAll : upcomingAll.filter((e) => e.absStart < cutoffAbs)),
    [upcomingAll, cutoffAbs],
  );
  const beyondHorizon = upcomingAll.length - upcoming.length;

  // The running job holds the press — it heads the sheet and cannot be moved.
  // Everything below it is the planned queue, and that is what reorders.
  const running = React.useMemo(() => upcoming.filter((e) => !isReorderable(e)), [upcoming]);
  const serverPlanned = React.useMemo(() => upcoming.filter(isReorderable), [upcoming]);
  const serverKey = serverPlanned.map((e) => e.id).join(',');

  const planned = React.useMemo(
    () => (pendingIds ? applyPendingOrder(serverPlanned, pendingIds) : serverPlanned),
    [serverPlanned, pendingIds],
  );

  // Let go of the optimistic order the moment the server agrees with it. The
  // timeout is the backstop for a PATCH that failed or landed differently — the
  // sheet must never sit on an order the schedule doesn't actually have.
  React.useEffect(() => {
    if (!pendingIds) return undefined;
    if (sameOrder(serverPlanned.map((e) => e.id), pendingIds)) {
      setPendingIds(null);
      return undefined;
    }
    const t = setTimeout(() => setPendingIds(null), PLACING_TIMEOUT_MS);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingIds, serverKey]);

  // A fresh open starts from the schedule as it stands.
  React.useEffect(() => {
    if (!open) { setPendingIds(null); setDragIdx(null); setInsertAt(null); }
  }, [open]);

  // While a move is in flight the committed times are stale — the server
  // re-cascades the whole lane. Show where the rows WILL fall, packed from the
  // queue anchor and paused across this lane's closures, and mark them as not
  // yet confirmed. Printing is blocked until they are.
  const provisional = React.useMemo(() => {
    if (!pendingIds) return null;
    return cascadeStarts({
      ordered: planned,
      anchorAbs: queueAnchor(laneEntries, nowAbs),
      windows: windows.filter((w) => w.press_code === lane?.code),
      baseMs: base.getTime(),
    });
  }, [pendingIds, planned, laneEntries, nowAbs, windows, lane?.code, base]);

  const commitMove = React.useCallback((from: number, to: number) => {
    if (to < 0 || to >= planned.length || from === to) return;
    const seq = sequenceForMove(planned, from, to);
    if (seq == null) return;
    setPendingIds(moveItem(planned, from, to).map((e) => e.id));
    onReorder(planned[from], seq);
  }, [planned, onReorder]);

  const onRowDragOver = (plannedIdx: number) => (ev: React.DragEvent) => {
    if (dragIdx == null) return;
    ev.preventDefault();
    ev.dataTransfer.dropEffect = 'move';
    const r = (ev.currentTarget as HTMLElement).getBoundingClientRect();
    setInsertAt(ev.clientY < r.top + r.height / 2 ? plannedIdx : plannedIdx + 1);
  };

  const endDrag = () => { setDragIdx(null); setInsertAt(null); };

  const onDrop = (ev: React.DragEvent) => {
    ev.preventDefault();
    if (dragIdx != null && insertAt != null) commitMove(dragIdx, finalIndex(dragIdx, insertAt));
    endDrag();
  };

  const rows = React.useMemo(() => [
    ...running.map((e) => ({ e, plannedIdx: null as number | null })),
    ...planned.map((e, i) => ({ e, plannedIdx: i })),
  ], [running, planned]);

  const totalSheets = rows.reduce((s, r) => s + (r.e.sheets || 0), 0);
  const generated = new Date().toLocaleString('en-NZ', {
    weekday: 'short', day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      {/* Print only the sheet: hide the app + dialog chrome, then reveal the
          run-sheet subtree and flatten it onto the page. */}
      <GlobalStyles styles={{
        '@media print': {
          'body *': { visibility: 'hidden' },
          [`#${PRINT_ROOT}, #${PRINT_ROOT} *`]: { visibility: 'visible' },
          [`#${PRINT_ROOT}`]: {
            position: 'absolute', left: 0, top: 0, width: '100%',
            margin: 0, padding: '8mm', boxShadow: 'none',
          },
          '.MuiDialog-container, .MuiPaper-root': {
            boxShadow: 'none !important', maxWidth: 'none !important',
            maxHeight: 'none !important', overflow: 'visible !important',
          },
          '.MuiDialogContent-root': { overflow: 'visible !important', padding: 0 },
          '.no-print': { display: 'none !important' },
          tr: { pageBreakInside: 'avoid' },
          '@page': { size: 'landscape', margin: '10mm' },
        },
      }} />
      <DialogContent>
        <Box id={PRINT_ROOT}>
          {/* masthead */}
          <Box sx={{
            display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between',
            gap: 2, pb: 1, mb: 1.5, borderBottom: `2px solid ${TOKENS.ink}`,
          }}>
            <Box>
              <Box sx={{ fontSize: 20, fontWeight: 700, color: TOKENS.ink, letterSpacing: '-0.01em' }}>
                Press Run Sheet · {lane?.code ?? '—'}
              </Box>
              <Box sx={{ fontSize: 11, color: TOKENS.ink2, mt: 0.25 }}>
                Pre-Press plate &amp; material preparation · scheduled order, top to bottom
                {/* The coverage prints: a paper copy must say what it spans. */}
                {horizonHr != null && (
                  <Box component="span" sx={{ fontWeight: 700, color: TOKENS.ink }}>
                    {' '}· next {horizonHr} h (to {dayLabel(base, cutoffAbs!)} {fmtClock(cutoffAbs!)})
                  </Box>
                )}
              </Box>
            </Box>
            <Box sx={{ textAlign: 'right', fontSize: 10.5, color: TOKENS.ink2, whiteSpace: 'nowrap' }}>
              <Box>Generated {generated}</Box>
              <Box sx={{ fontWeight: 700, color: TOKENS.ink, mt: 0.25 }}>
                {rows.length} job{rows.length === 1 ? '' : 's'} · {totalSheets.toLocaleString()} sheets
                {beyondHorizon > 0 && (
                  <Box component="span" sx={{ fontWeight: 400, color: TOKENS.ink2 }}>
                    {' '}· +{beyondHorizon} later
                  </Box>
                )}
              </Box>
            </Box>
          </Box>

          {/* Horizon picker — screen only; the masthead carries it into print. */}
          <Box className="no-print" sx={{
            display: 'flex', alignItems: 'center', gap: 1, mb: 1,
            fontSize: 11, color: TOKENS.ink2,
          }}>
            <Box sx={{ fontWeight: 700, color: TOKENS.ink3, textTransform: 'uppercase', fontSize: 9.5, letterSpacing: '0.04em' }}>
              Horizon
            </Box>
            <ToggleButtonGroup
              size="small" exclusive
              value={horizonHr ?? 'all'}
              onChange={(_, v) => { if (v != null) setHorizonHr(v === 'all' ? null : v); }}
            >
              {RUN_SHEET_HORIZON_HRS.map((h) => (
                <ToggleButton key={h} value={h} sx={{ textTransform: 'none', fontSize: 11.5, px: 1.25, py: 0.25 }}>
                  {h} h
                </ToggleButton>
              ))}
              <ToggleButton value="all" sx={{ textTransform: 'none', fontSize: 11.5, px: 1.25, py: 0.25 }}>
                All
              </ToggleButton>
            </ToggleButtonGroup>
            {beyondHorizon > 0 && (
              <Box>
                {beyondHorizon} job{beyondHorizon === 1 ? '' : 's'} beyond the horizon hidden
              </Box>
            )}
          </Box>

          {/* Reorder affordance — screen only. Says what a drag actually does,
              because it changes the press schedule for everyone, not this page. */}
          {planned.length > 1 && (
            <Box className="no-print" sx={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              gap: 1, mb: 1, px: 1, py: 0.6, borderRadius: `${TOKENS.radiusChip}px`,
              bgcolor: 'rgba(203,96,21,0.06)', border: `1px solid rgba(203,96,21,0.18)`,
              fontSize: 11, color: TOKENS.ink2,
            }}>
              <Box>
                <Box component="span" sx={{ fontWeight: 700, color: TOKENS.ink }}>Drag a row</Box>
                {' '}— or use the ▲▼ buttons — to change the run order. This reschedules the
                press: the board and the projected times update with it.
              </Box>
              {pendingIds && (
                <Box sx={{
                  display: 'flex', alignItems: 'center', gap: 0.5, whiteSpace: 'nowrap',
                  fontWeight: 700, color: TOKENS.accent,
                }}>
                  Recalculating times…
                </Box>
              )}
            </Box>
          )}

          {rows.length === 0 ? (
            <Box sx={{ py: 4, textAlign: 'center', fontSize: 13, color: TOKENS.ink3 }}>
              {beyondHorizon > 0
                ? `Nothing starts on ${lane?.code} in the next ${horizonHr} h — ${beyondHorizon} job${beyondHorizon === 1 ? '' : 's'} scheduled later.`
                : `No upcoming jobs scheduled on ${lane?.code}.`}
            </Box>
          ) : (
            <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
              <Box component="thead">
                <Box component="tr">
                  <Th className="no-print"> </Th>
                  <Th>#</Th>
                  <Th>Start</Th>
                  <Th>Job</Th>
                  <Th>Cust</Th>
                  <Th>Item code</Th>
                  <Th>Description</Th>
                  <Th>Forme</Th>
                  <Th>Raw material</Th>
                  <Th>Coating</Th>
                  <Th align="right">Size&nbsp;mm</Th>
                  <Th align="right">Sheets</Th>
                  <Th>Separations</Th>
                </Box>
              </Box>
              <Box component="tbody">
                {rows.map(({ e, plannedIdx }, i) => {
                  const inks = e.seps.filter((s) => s.role !== 'coating');
                  const coat = e.seps.find((s) => s.role === 'coating');
                  const artworkMissing = e.artworkStatus === 'not_found' || e.artworkStatus === 'error';
                  // Still queued in the artwork fetch. On a sheet that goes to
                  // the press, an em-dash would read as "this job has no inks",
                  // so say the separations simply haven't been read.
                  const artworkPending = e.artworkStatus === undefined;
                  // A second pass prints the REVERSE side, whose artwork we hold
                  // no record of anywhere — so this row's separations, which were
                  // resolved from the front, must not be shown against it.
                  const backSide = (e.pass ?? 1) > 1;
                  const movable = plannedIdx != null;
                  const prov = provisional?.get(e.id) ?? null;
                  const absStart = prov ? prov.absStart : e.absStart;
                  const dragging = movable && dragIdx === plannedIdx;
                  const edgeTop = movable && insertAt === plannedIdx;
                  const edgeBottom = movable
                    && plannedIdx === planned.length - 1 && insertAt === planned.length;
                  return (
                    <Box
                      component="tr"
                      key={e.id}
                      draggable={movable}
                      onDragStart={movable ? (ev: React.DragEvent) => {
                        ev.dataTransfer.effectAllowed = 'move';
                        // Firefox refuses to start a drag without payload.
                        ev.dataTransfer.setData('text/plain', String(e.id));
                        setDragIdx(plannedIdx);
                        setInsertAt(plannedIdx);
                      } : undefined}
                      onDragOver={movable ? onRowDragOver(plannedIdx!) : undefined}
                      onDrop={movable ? onDrop : undefined}
                      onDragEnd={endDrag}
                      sx={{
                        cursor: movable ? 'grab' : undefined,
                        opacity: dragging ? 0.4 : 1,
                        '&:hover .rs-grip': { opacity: 1 },
                        '& > td': {
                          bgcolor: dragging ? 'rgba(203,96,21,0.06)' : undefined,
                          boxShadow: edgeTop
                            ? `inset 0 2px 0 0 ${TOKENS.accent}`
                            : edgeBottom ? `inset 0 -2px 0 0 ${TOKENS.accent}` : undefined,
                        },
                      }}
                    >
                      <Td className="no-print">
                        {movable ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.1 }}>
                            <DragIndicatorRoundedIcon
                              className="rs-grip"
                              sx={{ fontSize: 16, color: TOKENS.ink3, opacity: 0.35, transition: 'opacity .12s' }}
                            />
                            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                              <IconButton
                                size="small" disableRipple
                                aria-label={`Move job ${e.job} earlier`}
                                disabled={plannedIdx === 0}
                                onClick={() => commitMove(plannedIdx!, plannedIdx! - 1)}
                                sx={{ p: 0, height: 13, color: TOKENS.ink3, '&:hover': { color: TOKENS.accent } }}
                              >
                                <KeyboardArrowUpRoundedIcon sx={{ fontSize: 15 }} />
                              </IconButton>
                              <IconButton
                                size="small" disableRipple
                                aria-label={`Move job ${e.job} later`}
                                disabled={plannedIdx === planned.length - 1}
                                onClick={() => commitMove(plannedIdx!, plannedIdx! + 1)}
                                sx={{ p: 0, height: 13, color: TOKENS.ink3, '&:hover': { color: TOKENS.accent } }}
                              >
                                <KeyboardArrowDownRoundedIcon sx={{ fontSize: 15 }} />
                              </IconButton>
                            </Box>
                          </Box>
                        ) : (
                          <Tooltip title="On the press — can't be moved" placement="right">
                            <LockOutlinedIcon sx={{ fontSize: 13, color: TOKENS.ink3, opacity: 0.5 }} />
                          </Tooltip>
                        )}
                      </Td>
                      <Td mono>{i + 1}</Td>
                      <Td mono>
                        <Box sx={{ opacity: prov ? 0.55 : 1 }}>
                          {dayLabel(base, absStart)}
                          <Box sx={{ fontSize: 9.5, color: TOKENS.ink3 }}>{fmtClock(absStart)}</Box>
                        </Box>
                        {e.status === 'in_progress' && (
                          <Box sx={{ fontSize: 8.5, fontWeight: 800, color: TOKENS.good, letterSpacing: '.3px' }}>RUNNING</Box>
                        )}
                      </Td>
                      <Td mono bold>
                        {e.job}
                        {e.passCount > 1 && (
                          <Box sx={{ fontSize: 8.5, fontWeight: 800, color: TOKENS.ink2, letterSpacing: '.3px' }}>
                            PASS {e.pass}/{e.passCount}
                          </Box>
                        )}
                      </Td>
                      <Td bold>{e.cust}</Td>
                      <Td mono>{e.ref || '—'}</Td>
                      <Td>{e.desc || '—'}</Td>
                      <Td mono>{e.forme ?? '—'}</Td>
                      <Td mono>{e.stockCode ?? '—'}</Td>
                      <Td>{e.coating ?? '—'}</Td>
                      <Td align="right" mono>
                        {e.length && e.width ? `${Math.round(e.length)}×${Math.round(e.width)}` : '—'}
                      </Td>
                      <Td align="right" mono>{(e.sheets || 0).toLocaleString()}</Td>
                      <Td>
                        {backSide ? (
                          // NEVER the front's colours on a back-side row. This
                          // sheet is a floor instruction: an operator reading a
                          // front ink strip against a back-side pass can set up
                          // the wrong plates. The reverse side's artwork is not
                          // recorded anywhere in the business (not Prism, no
                          // Cloudflow convention, no side on prepress.artwork_record),
                          // so the honest answer is "we don't hold this", not a
                          // plausible-looking strip belonging to the other side.
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, color: TOKENS.warn, fontWeight: 700, fontSize: 9.5 }}>
                            <WarningAmberRoundedIcon sx={{ fontSize: 12 }} />
                            BACK SIDE — ARTWORK NOT RECORDED
                          </Box>
                        ) : artworkMissing ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, color: TOKENS.warn, fontWeight: 700, fontSize: 9.5 }}>
                            <WarningAmberRoundedIcon sx={{ fontSize: 12 }} />
                            NO ARTWORK
                          </Box>
                        ) : (
                          <Box sx={{ display: 'flex', gap: 0.4, flexWrap: 'wrap' }}>
                            {/* Swatch + code: the code is what survives a mono printer. */}
                            {inks.map((s, k) => (
                              <Box key={k} sx={{
                                minWidth: 20, px: 0.4, height: 15, borderRadius: '3px',
                                background: s.hex, color: contrastText(s.hex),
                                border: '0.5px solid rgba(0,0,0,.35)',
                                display: 'grid', placeItems: 'center',
                                fontSize: 8.5, fontWeight: 800, letterSpacing: '.2px',
                                printColorAdjust: 'exact', WebkitPrintColorAdjust: 'exact',
                              }}>{s.short}</Box>
                            ))}
                            {coat && (
                              <Box sx={{
                                minWidth: 20, px: 0.4, height: 15, borderRadius: '3px',
                                border: '1px dashed #6b7a88', color: TOKENS.ink2,
                                display: 'grid', placeItems: 'center', fontSize: 8.5, fontWeight: 800,
                              }}>VAR</Box>
                            )}
                            {inks.length === 0 && !coat && (
                              <Box sx={{ fontSize: 9, color: TOKENS.ink3, fontStyle: artworkPending ? 'italic' : 'normal' }}>
                                {artworkPending ? 'not read' : '—'}
                              </Box>
                            )}
                          </Box>
                        )}
                      </Td>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}

          {/* Prints on purpose: a horizon-limited sheet must not read as "the
              queue ends here" to whoever holds the paper. */}
          {beyondHorizon > 0 && rows.length > 0 && (
            <Box sx={{ mt: 1, py: 0.6, px: 0.75, fontSize: 10, fontWeight: 700, color: TOKENS.ink2, borderTop: `1.5px solid ${TOKENS.ink3}` }}>
              + {beyondHorizon} more job{beyondHorizon === 1 ? '' : 's'} scheduled beyond the {horizonHr} h horizon — not shown.
            </Box>
          )}

          <Box sx={{ mt: 2, pt: 1, borderTop: `1px solid ${TOKENS.line}`, fontSize: 9, color: TOKENS.ink3 }}>
            Separations are read live from Cloudflow artwork. A “NO ARTWORK” row means the item code
            has no matching PDF — check the item code / Pre-Press file naming before plating.
          </Box>
        </Box>
      </DialogContent>
      <DialogActions className="no-print">
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>Close</Button>
        {/* Never print a sheet whose start times the server hasn't confirmed —
            a paper copy outlives the screen it was taken from. */}
        <Tooltip title={pendingIds ? 'Waiting for the new times to come back from the schedule' : ''}>
          <Box component="span">
            <Button
              variant="contained" disableElevation startIcon={<PrintRoundedIcon />}
              disabled={!!pendingIds}
              onClick={() => window.print()}
              sx={{ textTransform: 'none', bgcolor: TOKENS.accent, '&:hover': { bgcolor: '#b25313' } }}
            >
              Print
            </Button>
          </Box>
        </Tooltip>
      </DialogActions>
    </Dialog>
  );
}
